import React, { useState, useRef, FormEvent, useEffect } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Paper } from '@mui/material';
import { LocationInput, ISearchLocationPayload } from "@components/atoms/location-search";

export function NextJsLanding() {
  const backgroundImages = [
    'assets/images/img1.jpg',
    'assets/images/img2.jpg',
    'assets/images/img3.jpg',
    'assets/images/img4.jpg',
    'assets/images/img5.jpg',
    'assets/images/img6.jpg',
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const [address, setAddress] = useState<string>('');
  const [coordinates, setCoordinates] = useState([]);
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const navigate = useNavigate();
  const addressInputRef = useRef<HTMLInputElement>(null);
  const ldClient = useLDClient();

  // LaunchDarkly heading flag logic
  const { copyFlag } = useFlags();
  const heading = copyFlag
    ? "Hyper-Local Concierge Available"
    : "Need Help? We've Got You Covered"

  const urlDate = {
    address,
    cordinates: coordinates,
    zipCode,
    city,
  };

  // Platform detection
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleOrderStart = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    navigate('/caterers', { state: urlDate });
  };

  return (
    <Box component="main" sx={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif', color: '#1f2937' }}>
      {/* Hero section */}
      <Box component="section" sx={{ position: 'relative', pt: { xs: 6, md: 12 }, pb: { xs: 6, md: 10 } }}>
        {/* Background cross-fade images */}
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
          {backgroundImages.map((src, index) => (
            <Box
              key={src}
              component="img"
              src={src}
              alt={`Background ${index + 1}`}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: currentImageIndex === index ? 0.8 : 0,
                transition: 'opacity 1s ease-in-out',
                zIndex: 1,
              }}
            />
          ))}
          {/* Gradient overlay remains on top */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.5))',
              zIndex: 3,
            }}
          />
        </Box>
        <Container sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 700,
              color: '#fff',
              mb: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.7)',
              lineHeight: 1.1,
            }}
          >
            Reliable, Corporate Catering that Rewards!
          </Typography>
          <Typography
            component="p"
            sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, color: '#fff', mb: 4, maxWidth: 600, mx: 'auto', textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}
          >
            Enter your delivery address below to discover local caterers and start building your order. Get instant pricing, browse menus, and place your catering order in minutes.
          </Typography>
          <Box
            component="form"
            onSubmit={handleOrderStart}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              maxWidth: 800,
              mx: 'auto',
              mt: 2,
              px: { xs: 2, md: 0 },
            }}
          >
            <LocationInput
              placeholder="Enter your delivery address..."
              noOptionsText="Enter your location"
              InputLabelProps={{ shrink: false }}
              value={address}
              onChange={(val: string) => setAddress(val)}
              onChangeLocationOptions={(val: ISearchLocationPayload) => {
                setCoordinates(val.coordinates);
                setCity(val.city);
                setZipCode(val.zip);
              }}
              sx={{
                bgcolor: '#fff',
                borderRadius: '4px',
                flex: 1,
                width: '100%'
              }}
              inputProps={{
                sx: { height: 56, px: 0 },
                inputRef: addressInputRef,
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                fontWeight: 600,
                height: 56,
                whiteSpace: 'nowrap',
                mt: { xs: 2, md: 0 },
                ml: { md: 2 }
              }}
            >
              Start Your Order
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Support section */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#f3f4f6' }}>
        <Container>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1f2937' }}>
              {heading}
            </Typography>
            <Typography variant="h6" sx={{ color: '#4b5563', mb: 4, lineHeight: 1.5 }}>
              Dietary restrictions? Can't find your favorite restaurant? Special requests? Our concierge teams handle the details so you don't have to!
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic', mb: 2 }}>
              Available Every Day - 8AM-8PM EST
            </Typography>
            <Typography variant="h5" sx={{ color: '#FD4E3B', fontWeight: 600 }}>
              <a href="tel:+16465354875" style={{ color: '#FD4E3B', textDecoration: 'none' }}>(646) 535-4875</a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Features section */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#fff' }}>
        <Container>
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 2 }}>
            Why Businesses Choose Caterplace
          </Typography>
          <Typography variant="subtitle1" align="center" sx={{ color: '#4b5563', mb: 6 }}>
            We combine effortless ordering with exceptional service to redefine your catering experience.
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            {[
              { title: 'Fast, Reliable Catering', icon: '../assets/images/feature1.png', text: 'Rest easy knowing your order is triple-confirmed and arrives exactly when you need it.' },
              { title: 'Hyper-Local Concierge', icon: '../assets/images/feature2.png', text: 'Our expert concierge team is ready to assist you personally, ensuring every order goes smoothly.' },
              { title: 'Hassle-Free Ordering', icon: '../assets/images/feature3.png', text: 'Simplify your catering logistics—easily browse menus, compare caterers, and schedule orders online.' },
              { title: 'Best Local Caterers', icon: '../assets/images/feature4.png', text: 'We partner closely with carefully vetted restaurants to guarantee outstanding quality and service every time.' },
              { title: 'Real People, Ready by Phone', icon: '../assets/images/feature5.png', text: 'Need assistance or have special requests? A dedicated support specialist is just a phone call away.' },
              { title: 'Rewarding Every Order', icon: '../assets/images/feature6.png', text: 'Earn up to 15x rewards on every catering order just for choosing Caterplace.' },
            ].map((feature) => (
              <Grid item xs={12} sm={6} md={4} key={feature.title}>
                <Paper
                  elevation={3}
                  sx={{
                    boxShadow: '0px 4px 10px rgba(30, 41, 59, 0.1)', // navy blue shadow
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#fff',
                    color: '#000',
                    // border: '2px solid #1e293b',
                    borderRadius: '15px',
                    p: 4,
                    textAlign: 'center',
                    '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box
                    component="img"
                    src={feature.icon}
                    alt={feature.title}
                    sx={{
                      height: 80,
                      width: 'auto',
                      maxWidth: '100%',
                      mb: 2,
                      alignSelf: 'center',
                      flexShrink: 0,
                      filter: 'invert(100%)',
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {feature.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Occasions section */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#f3f4f6' }}>
        <Container>
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 6 }}>
            Catering for Every Occasion
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            {[
              { title: 'Lunch Catering', image: '../assets/images/image1.jpg', text: 'Make midday meals memorable with diverse lunch options—from sandwiches to gourmet entrees—perfect for meetings, events, or everyday office dining.', link: 'https://blog.caterplace.com/discover-premier-lunch-catering-in-nyc-with-caterplace/' },
              { title: 'Breakfast Catering', image: '../assets/images/image2.jpg', text: 'Fuel productivity with delicious breakfasts, including fresh pastries, hot coffee, bagels, and more—delivered right when your team needs them.', link: 'https://blog.caterplace.com/making-office-catering-a-breeze-how-caterplace-changes-the-game-for-nyc-executive-assistants/' },
              { title: 'Same-Day Catering', image: '../assets/images/image3.jpg', text: 'Need catering fast? Caterplace offers quick, reliable same-day catering for last‑minute meetings or surprise events.', link: 'https://blog.caterplace.com/same-day-catering-in-nyc-your-ultimate-guide-to-quick-delicious-and-reliable-catering/' },
              { title: 'Your Corporate Catering Platform', image: '../assets/images/image4.jpg', text: 'Easily manage recurring meals, dietary preferences, multiple locations, and streamlined billing through one simple platform.', link: 'https://blog.caterplace.com/business-catering-nyc-top-office-catering-platforms-2025-comparison/' },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Paper
                  elevation={3}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box component="img" src={item.image} alt={item.title} sx={{ width: '100%', height: 200, objectFit: 'cover' }} />
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4b5563' }}>
                      {item.text}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        component="a"
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read More →
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials section */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#fff' }}>
        <Container disableGutters maxWidth={false} sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 2 }}>
              Why People Love Caterplace
            </Typography>
            <Typography variant="subtitle1" align="center" sx={{ color: '#4b5563', mb: 6 }}>
              Join hundreds of satisfied professionals who rely on Caterplace for stress-free, reliable catering.
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {[
                { name: 'Ally', role: 'Executive Administrator', avatar: '../assets/images/ally.png', quote: '“Ordering lunch for my staff used to be the most stressful part of my day, and now it’s the best. They have a ton of restaurants to choose from that also offer individually wrapped options.”' },
                { name: 'Merissa', role: 'Account Executive', avatar: '../assets/images/merissa.png', quote: '“Caterplace understands me. I like to eat and I like to go shopping. Caterplace literally rewards me on EVERY catering order. Then, I use my CaterCash rewards and go shopping on Amazon!”' },
              ].map((t) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={t.name}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <Paper elevation={3} sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9fafb', width: '100%', maxWidth: 560, mx: 'auto' }}>
                    <Box component="img" src={t.avatar} alt={t.name} sx={{ width: 80, height: 80, borderRadius: '50%', mb: 2, objectFit: 'cover' }} />
                    <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2, color: '#4b5563' }}>
                      {t.quote}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {t.role}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 6, gap: 4, flexWrap: 'wrap' }}>
              {['logo1.png', 'logo2.png', 'stripe.png', 'logo3.png', 'logo4.png'].map((logo) => (
                <Box
                  key={logo}
                  component="img"
                  src={`../assets/images/${logo}`}
                  alt="Partner Logo"
                  sx={{
                    height: 64,
                    width: 'auto',
                    filter: 'grayscale(100%)',
                    opacity: 0.7,
                    '&:hover': { filter: 'grayscale(0)', opacity: 1 },
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA section */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#1f283b', color: '#fff', textAlign: 'center' }}>
        <Container>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
            Ready to Simplify Your Catering?
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 4 }}>
            Start your first order or sign up today to experience how effortless corporate catering can be.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                addressInputRef.current?.focus();
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Start Your Order
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
